import { useEffect, useMemo } from 'react';
import { usePreservedCallback } from '@toss/react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { ZepApiError, ZepApiStatusType } from '@zep/apis';
import { redirectHome } from '@zep/common/routes';
import { useDialog } from '@zep/dialog';
import { Button } from '@zep/ui';
import { ErrorPageWrapper, ErrorWidget } from '@zep/web-components';
export type PlanPaymentClientErrorMessage = 'ALREADY_CUSTOM_ONETIME_PLAN_EXISTS' | 'ALREADY_ACTIVE_ENTERPRISE_PLAN_EXISTS' | 'ALREADY_ONETIME_READY_PLAN_EXISTS' | 'MOBILE_DEVICE_NOT_SUPPORTED';
export class PlanPaymentClientError extends Error {
  constructor(message: PlanPaymentClientErrorMessage) {
    super(message);
  }
}
export const SubscriptionRejectedFallback = ({
  error,
  reset
}: SubscriptionRejectedFallbackProps) => {
  const router = useRouter();
  const {
    t
  } = useTranslation(['subscription', 'common']);
  const dialog = useDialog();
  const redirectPlan = usePreservedCallback(() => {
    if (router.query.spaceHashId) {
      router.replace(`/me/spaces/${router.query.spaceHashId}/plan`);
      return;
    }
    redirectHome();
  });
  const redirectChannelTalk = usePreservedCallback(() => {
    window.location.href = t('subscription:plans.contact.channelTalkLink');
  });
  const redirectPricing = usePreservedCallback(() => {
    router.replace('/pricing');
  });
  const showAlert = usePreservedCallback((title: string, content: string, confirmAction: () => void) => {
    dialog.alert({
      title,
      content
    }).then(() => {
      confirmAction();
    });
  });
  const alertMappings = useMemo(() => {
    return {
      [String(ZepApiStatusType.FAILED)]: () => showAlert(t('subscription:payment.alertFailPay.title'), t('subscription:checkout.currentPlanFail.content'), redirectPlan),
      [String(ZepApiStatusType.NO_PERMISSION_TO_MANAGE_PREMIUM_PLAN)]: () => showAlert(t('subscription:payment.alertFailPay.title'), t('subscription:payment.enter.failToManageContent'), redirectHome),
      [String(ZepApiStatusType.ALREADY_READY_SPACE_PLAN_EXISTS)]: () => showAlert(t('subscription:payment.alertFailPay.title'), t('subscription:payment.enter.hasReadyCustomContent'), redirectChannelTalk)
    };
  }, [showAlert, t, redirectPlan, redirectChannelTalk]);
  useEffect(() => {
    if (error instanceof ZepApiError && alertMappings[error.status]) {
      alertMappings[error.status]();
    }
    return () => {
      if (reset) {
        reset();
      }
    };
  }, [error, reset, alertMappings]);
  useEffect(() => {
    if (error instanceof PlanPaymentClientError) {
      switch (error.message) {
        case 'ALREADY_CUSTOM_ONETIME_PLAN_EXISTS':
        case 'ALREADY_ACTIVE_ENTERPRISE_PLAN_EXISTS':
          {
            return showAlert(t('subscription:paymentError.contactPlan.title', '문의를 통해 추가된 플랜이 있습니다'), t('subscription:paymentError.contactPlan.description', '다른 기간에 플랜을 구매하고 싶거나 변경하고 싶은 경우 별도 문의해 주시기 바랍니다.'), redirectChannelTalk);
          }
        case 'ALREADY_ONETIME_READY_PLAN_EXISTS':
          {
            return showAlert(t('subscription:paymentError.stillReady.title', '아직 플랜이 활성화되지 않았습니다'), t('subscription:paymentError.stillReady.description', '구매된 플랜의 시작일이 아직 남았습니다. 플랜 변경은 시작일 이후에 가능합니다.'), redirectChannelTalk);
          }
        case 'MOBILE_DEVICE_NOT_SUPPORTED':
          {
            return showAlert(t('subscription:paymentError.blockedMobile.title', '프리미엄 플랜 결제는 PC 환경에서만 이용 가능합니다.'), '', redirectPricing);
          }
      }
    }
  }, [error, showAlert, t, redirectChannelTalk, redirectPricing]);
  return <ErrorPageWrapper data-sentry-element="ErrorPageWrapper" data-sentry-component="SubscriptionRejectedFallback" data-sentry-source-file="SubscriptionRejectedFallback.tsx">
      <ErrorSubscriptionRejectedWidget data-sentry-element="ErrorSubscriptionRejectedWidget" data-sentry-source-file="SubscriptionRejectedFallback.tsx" />
    </ErrorPageWrapper>;
};
type SubscriptionRejectedFallbackProps<ErrorType extends Error = Error> = {
  error?: ErrorType;
  reset?: (...args: unknown[]) => void;
};
export const ErrorSubscriptionRejectedWidget = () => {
  const {
    t,
    ready
  } = useTranslation(['common', 'subscription']);
  const imageUrl = '/images/error_500.png';
  const title = '';
  const description = t('common:error.500.content');
  if (!ready) return null;
  return <ErrorWidget.Root data-sentry-element="unknown" data-sentry-component="ErrorSubscriptionRejectedWidget" data-sentry-source-file="SubscriptionRejectedFallback.tsx">
      <ErrorWidget.Header data-sentry-element="unknown" data-sentry-source-file="SubscriptionRejectedFallback.tsx">
        <ErrorWidget.Image src={imageUrl} data-sentry-element="unknown" data-sentry-source-file="SubscriptionRejectedFallback.tsx" />
      </ErrorWidget.Header>
      <ErrorWidget.Content data-sentry-element="unknown" data-sentry-source-file="SubscriptionRejectedFallback.tsx">
        <ErrorWidget.Title data-sentry-element="unknown" data-sentry-source-file="SubscriptionRejectedFallback.tsx">{title}</ErrorWidget.Title>
        <ErrorWidget.Description data-sentry-element="unknown" data-sentry-source-file="SubscriptionRejectedFallback.tsx">{description}</ErrorWidget.Description>
      </ErrorWidget.Content>
      <ErrorWidget.Footer data-sentry-element="unknown" data-sentry-source-file="SubscriptionRejectedFallback.tsx">
        <Button key={1} size={'lg'} as="a" href={'/'} data-sentry-element="Button" data-sentry-source-file="SubscriptionRejectedFallback.tsx">
          {t('subscription:subscription.common.gohome')}
        </Button>
      </ErrorWidget.Footer>
    </ErrorWidget.Root>;
};